import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Modal, Typography, message } from 'antd'
import { Link } from 'react-router-dom'
import moment from 'moment'

import { Case, CaseFlag } from '../..'
import { retractCase } from '../../../../api/arendeApi'
import { useApi } from '../../../../api/useApi'
import { getMe } from '../../../../store/organizationSlice'
import { PermissionTypes } from '../../../../components/menus/utils/generateSideMenuChildren'
import { KIND_LABEL, reasonLabel } from './reasons'

const { Text } = Typography

/**
 * Vilken behörighet en länk till flaggans detaljvy kräver, per måltyp.
 *
 * Rutten till flaggvyn släpper in den som har NÅGON av flagg-behörigheterna, och
 * `uppföljningslista` räcker — men sidan hämtar sin text från en endpoint som är
 * spärrad PER FLAGGTYP. En avtalsansvarig med personal och utförare men utan brukare
 * kom alltså fram till en sida som svarade 403 och såg trasig ut. Länken visas därför
 * bara när behörigheten finns; annars står flaggnamnet som text.
 */
const PERMISSION_FÖR_MÅL: Record<string, string> = {
  b: PermissionTypes.brukare,
  m: PermissionTypes.personal,
  u: PermissionTypes.utförare,
}

interface Props {
  /** Hanteringen som visas. Null = stängd. */
  hantering: Case | null
  onClose: () => void
  /** Anropas när hanteringen ångrats — listan behöver hämtas om. */
  onRetracted: () => void
}

/**
 * Detaljer om en hantering (#30).
 *
 * Ersätter sidan `/app/arenden/:id` för avfärdad och hanterad. De två är avslutade
 * konstateranden — det finns ingenting att uppdatera, bara att läsa och eventuellt
 * ångra. Då är en modal bättre än en sida: man stannar kvar i listan i stället för att
 * navigera bort och tillbaka för att göra ingenting. Sidan lever kvar för
 * uppföljningar, som har en riktig livscykel med anteckningar och stängning.
 *
 * Det enda som inte redan står som kolumn i listan är VILKA flaggor hanteringen
 * omfattar — resten av modalen är sammanhang runt den listan.
 */
const HandlingDetailsModal = ({ hantering, onClose, onRetracted }: Props) => {
  const api = useApi()
  const me = useSelector(getMe)
  const [retracting, setRetracting] = useState(false)

  if (!hantering) return null

  const retract = async () => {
    setRetracting(true)

    try {
      await retractCase(api, hantering.id)
      message.success('Hanteringen är ångrad')
      onRetracted()
    } catch (e) {
      message.error('Kunde inte ångra hanteringen')
    } finally {
      setRetracting(false)
    }
  }

  const bekräftaÅngra = () =>
    Modal.confirm({
      title: 'Ångra hanteringen?',
      // Påstår INTE att flaggorna blir ohanterade: en flagga kan ha flera hanteringar,
      // och den här vyn känner bara till sin egen. Flaggans detaljvy vet mer.
      content:
        hantering.flags.length > 1
          ? `Hanteringen gäller ${hantering.flags.length} flaggor och slutar gälla för alla. Hanteringen raderas inte, den slutar bara gälla.`
          : 'Hanteringen slutar gälla för flaggan. Hanteringen raderas inte, den slutar bara gälla.',
      okText: 'Ångra',
      cancelText: 'Avbryt',
      okButtonProps: { danger: true },
      onOk: retract,
    })

  const flaggnamn = (f: CaseFlag) => {
    const krävs = PERMISSION_FÖR_MÅL[f.target_type]
    if (krävs && !me.permissions?.includes(krävs)) return <Text>{f.namn}</Text>

    return (
      <Link
        to={{
          pathname: `/app/avvikelse/flag=${f.type}&id=${f.id}`,
          // Flaggvyn läser rubriken ur route-state och kraschar utan den.
          state: { flagname: f.namn, riskniva: f.nivå },
        }}
      >
        {f.namn}
      </Link>
    )
  }

  const rad = (etikett: string, värde: React.ReactNode) => (
    <div style={{ marginBottom: 8 }}>
      <Text strong>{etikett}: </Text>
      {värde}
    </div>
  )

  return (
    <Modal
      open
      title={`Hantering ${hantering.key}`}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="ångra" danger loading={retracting} onClick={bekräftaÅngra}>
          Ångra hantering
        </Button>,
        <Button key="stäng" onClick={onClose}>
          Stäng
        </Button>,
      ]}
    >
      {rad('Handling', KIND_LABEL[hantering.kind] ?? hantering.kind)}
      {!!hantering.sub_reasons?.length && rad('Skäl', hantering.sub_reasons.map(reasonLabel).join(' · '))}
      {rad('Hanterad av', hantering.ägare.namn)}
      {rad('Hanterad', moment(hantering.created).format('YYYY-MM-DD, HH:mm'))}

      <div style={{ marginTop: 20 }}>
        <Text strong>Flaggor ({hantering.flags.length})</Text>
        <div style={{ marginTop: 8, maxHeight: 320, overflowY: 'auto' }}>
          {hantering.flags.map((f) => (
            <div key={`${f.type}:${f.id}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '4px 0' }}>
              {flaggnamn(f)}
              <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                Risknivå {f.nivå} · Skapad {moment(f.created).format('YYYY-MM-DD')}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  )
}

export default HandlingDetailsModal
